import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Download } from "lucide-react";
import { KineticLine, Reveal, Overline } from "@/components/Kinetic";
import { PRODUCTS, IMAGES } from "@/data/site";

export default function Products() {
  const flagship = PRODUCTS[0];
  const rest = PRODUCTS.slice(1);

  return (
    <main data-testid="products-page">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-6 pb-20 pt-24 lg:px-10 lg:pt-32" data-testid="products-hero">
        <Overline>Our Commodity Portfolio</Overline>
        <h1 className="mt-6 font-serif font-light leading-[0.95] tracking-tight text-cream" data-testid="products-heading">
          <KineticLine delay={0.25} className="text-5xl md:text-6xl lg:text-7xl">
            Six Portfolios.
          </KineticLine>
          <KineticLine delay={0.4} className="text-5xl md:text-6xl lg:text-7xl">
            One Standard<span className="text-gold">.</span>
          </KineticLine>
        </h1>
        <Reveal delay={0.7} className="mt-8 max-w-2xl">
          <p className="text-base font-light leading-relaxed text-sage md:text-lg">
            From essential staples to specialty commodities — each handled with care, precision and
            global quality standards. Sourced responsibly. Tested thoroughly. Delivered reliably.
          </p>
        </Reveal>
        <Reveal delay={0.85} className="mt-10">
          <a
            href="/catalogue/nishmayy-global-catalogue.pdf"
            download
            data-testid="products-catalogue-button"
            className="group inline-flex items-center gap-3 border border-gold px-8 py-4 text-sm font-semibold tracking-wide text-gold transition-colors duration-300 hover:bg-gold hover:text-forest"
          >
            <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            Download Catalogue
          </a>
        </Reveal>
      </section>

      {/* FLAGSHIP: DRY FRUITS */}
      <section className="border-y border-forest-border bg-forest-surface" data-testid="products-flagship">
        <div className="mx-auto grid max-w-7xl gap-0 lg:grid-cols-2">
          <div className="relative min-h-[420px] overflow-hidden lg:min-h-[640px]">
            <motion.img
              src={flagship.image}
              alt="Premium dry fruits and nuts"
              initial={{ scale: 1.15 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, ease: [0.76, 0, 0.24, 1] }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-forest-surface/40 lg:bg-gradient-to-r" />
            <span className="absolute left-6 top-6 border border-gold/50 bg-forest/70 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur-sm" data-testid="flagship-badge">
              DryVeda · Processed &amp; Packed in New Delhi
            </span>
          </div>
          <div className="flex flex-col justify-center px-6 py-16 lg:px-16 lg:py-24">
            <Reveal><Overline>DryVeda · Our Flagship Processed Brand</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-cream md:text-5xl">
                {flagship.name}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-2 text-sm uppercase tracking-[0.2em] text-gold">{flagship.tagline}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base font-light leading-relaxed text-sage">{flagship.desc}</p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-8 flex flex-wrap gap-2.5" data-testid="flagship-varieties">
                {flagship.varieties.map((v) => (
                  <span key={v} className="border border-gold/30 bg-forest px-4 py-2 text-xs tracking-wide text-cream transition-colors duration-300 hover:border-gold hover:text-gold">
                    {v}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {["Stringent multi-stage quality checks", "Hygienic processing & grading", "Custom retail & bulk packaging", "Export-compliant documentation"].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm font-light text-sage">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2} />
                    {f}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.35} className="mt-10">
              <Link
                to="/contact"
                data-testid="flagship-enquire-button"
                className="group inline-flex items-center gap-3 bg-gold px-8 py-4 text-sm font-semibold tracking-wide text-forest transition-colors duration-300 hover:bg-cream"
              >
                Enquire for Dry Fruits
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CATEGORY GRID */}
      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-10" data-testid="products-grid">
        <Reveal><Overline>Agro Commodity Exports</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-4 font-serif text-4xl font-light text-cream md:text-5xl">The Full Portfolio</h2>
        </Reveal>
        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {rest.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 4) * 0.08}>
              <article
                className="group relative h-[420px] overflow-hidden border border-forest-border"
                data-testid={`product-card-${p.slug}`}
              >
                <img
                  src={p.image}
                  alt={p.name}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/70 to-forest/10 transition-opacity duration-500" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <h3 className="font-serif text-2xl font-light text-cream">{p.name}</h3>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-gold/90">{p.tagline}</p>
                </div>
                <div className="absolute inset-0 flex translate-y-4 flex-col justify-end bg-forest/95 p-6 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                  <h3 className="font-serif text-2xl font-light text-gold">{p.name}</h3>
                  <p className="card-clamp mt-3 text-sm font-light leading-relaxed text-sage">{p.desc}</p>
                  <div className="mt-4 flex min-h-[52px] flex-wrap content-start items-start gap-1.5">
                    {p.varieties.slice(0, 4).map((v) => (
                      <span key={v} className="border border-cream/20 px-2 py-1 text-[10px] text-cream/80">{v}</span>
                    ))}
                    {p.varieties.length > 4 && (
                      <span className="px-2 py-1 text-[10px] text-gold">+{p.varieties.length - 4} more</span>
                    )}
                  </div>
                  <Link
                    to="/contact"
                    data-testid={`product-enquire-${p.slug}`}
                    className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-gold"
                  >
                    Enquire <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FLAGSHIP: FOXNUTS (DRYVEDA) */}
      <section className="border-b border-forest-border bg-forest-surface" data-testid="products-foxnuts-flagship">
        <div className="mx-auto grid max-w-7xl gap-0 lg:grid-cols-2">
          <div className="order-2 flex flex-col justify-center px-6 py-16 lg:order-1 lg:px-16 lg:py-24">
            <Reveal><Overline>DryVeda · Processed Foxnuts</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-cream md:text-5xl">
                Foxnuts (Makhana)
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-2 text-sm uppercase tracking-[0.2em] text-gold">Nature's crunch. Harvested from Indian ponds.</p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-base font-light leading-relaxed text-sage">
                Premium phool makhana from Bihar's pond harvests — popped, graded and packed at our
                New Delhi unit under our DryVeda brand. Light, protein-rich, gluten-free and ready for
                retail shelves or bulk export programs.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-8">
                <p className="text-xs uppercase tracking-[0.25em] text-sage">Export Grades</p>
                <div className="mt-3 flex flex-wrap gap-2.5" data-testid="foxnuts-grades">
                  {["10–12 Suta", "8–10 Suta", "6–8 Suta", "6 Suta & Below"].map((g) => (
                    <span key={g} className="border border-gold/30 bg-forest px-4 py-2 text-xs tracking-wide text-cream transition-colors duration-300 hover:border-gold hover:text-gold">
                      {g}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.3}>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                {["Moisture-controlled hygienic packing", "Retail pouches & bulk 5–25kg bags", "Custom flavours & private label", "Container-load export supply"].map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm font-light text-sage">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" strokeWidth={2} />
                    {f}
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.35} className="mt-10">
              <Link
                to="/contact"
                data-testid="foxnuts-pricing-button"
                className="group inline-flex items-center gap-3 bg-gold px-8 py-4 text-sm font-semibold tracking-wide text-forest transition-colors duration-300 hover:bg-cream"
              >
                Request Foxnut Pricing
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
          <div className="relative order-1 min-h-[420px] overflow-hidden lg:order-2 lg:min-h-[640px]">
            <motion.img
              src="/images/foxnuts.jpg"
              alt="DryVeda premium foxnuts (makhana)"
              initial={{ scale: 1.15 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1.6, ease: [0.76, 0, 0.24, 1] }}
              className="absolute inset-0 h-full w-full object-cover"
            />
            <span className="absolute left-6 top-6 border border-gold/50 bg-forest/70 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-gold backdrop-blur-sm" data-testid="foxnuts-flagship-badge">
              DryVeda · Graded &amp; Packed in New Delhi
            </span>
          </div>
        </div>
      </section>

      {/* QUALITY STRIP */}
      <section className="relative overflow-hidden border-t border-forest-border" data-testid="products-quality">
        <img src={IMAGES.dryFruitsJar} alt="Packed premium dry fruits" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-forest/85" />
        <div className="relative z-10 mx-auto max-w-7xl px-6 py-24 lg:px-10">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div>
              <Reveal><Overline>Quality You Can Trust</Overline></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-4 font-serif text-4xl font-light leading-tight text-cream md:text-5xl">
                  Handpicked for freshness. Packed with nutrition. Delivered with trust worldwide.
                </h2>
              </Reveal>
            </div>
            <div className="grid gap-px border border-forest-border bg-forest-border sm:grid-cols-2">
              {[
                ["Sourced Responsibly", "Trusted origins across India and select global regions"],
                ["Tested Thoroughly", "Multi-stage inspection and certification compliance"],
                ["Delivered Reliably", "On-time, every time — across 93+ countries"],
                ["Partnered Globally", "Long-term relationships that create value"],
              ].map(([t, d], i) => (
                <Reveal key={t} delay={i * 0.08} className="bg-forest">
                  <div className="px-6 py-8">
                    <h3 className="font-serif text-xl font-light text-gold">{t}</h3>
                    <p className="mt-2 text-sm font-light text-sage">{d}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
